import { Directive, ElementRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

/**
 * `[appCollapseHeight]` — anima abrir/fechar de um bloco pela ALTURA REAL
 * (0 ↔ altura medida do conteúdo), com `overflow: hidden`.
 *
 * Por que não CSS puro:
 *  - `max-height` anima até um teto fixo, mas a altura VISÍVEL trava no conteúdo
 *    → velocidade não-constante ("pausa" e "salta").
 *  - `grid-template-rows: 0fr↔1fr` deixa a superfície do container sobre a área
 *    ainda não revelada (bloco cobrindo o conteúdo).
 *
 * Medindo `scrollHeight` e animando `height` a gente tem VELOCIDADE CONSTANTE
 * (timing `linear`) e clip limpo (conteúdo só some/aparece, sem nada cobrindo).
 * Ao terminar de abrir, volta pra `height: auto` (acompanha conteúdo dinâmico).
 */
@Directive({
  selector: '[appCollapseHeight]',
})
export class CollapseHeightDirective implements OnInit, OnChanges {
  @Input('appCollapseHeight') expanded = false;
  /** Duração da animação (ms). */
  @Input() collapseDuration = 220;

  private onTransitionEnd = (e: TransitionEvent) => {
    if (e.propertyName === 'height' && this.expanded) {
      // Aberto: solta pra `auto` (cresce se o conteúdo mudar).
      this.el.nativeElement.style.height = 'auto';
    }
  };

  constructor(private el: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    const s = this.el.nativeElement.style;
    s.overflow = 'hidden';
    s.transitionProperty = 'height';
    s.transitionTimingFunction = 'linear';   // velocidade CONSTANTE
    s.transitionDuration = `${this.collapseDuration}ms`;
    // Estado inicial SEM animar.
    s.height = this.expanded ? 'auto' : '0px';
    this.el.nativeElement.addEventListener('transitionend', this.onTransitionEnd);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.expanded && !changes.expanded.firstChange) {
      this.animate(this.expanded);
    }
    if (changes.collapseDuration && !changes.collapseDuration.firstChange) {
      this.el.nativeElement.style.transitionDuration = `${this.collapseDuration}ms`;
    }
  }

  private animate(expand: boolean): void {
    const el = this.el.nativeElement;
    const target = this.contentHeight();
    if (expand) {
      // De 0 até a altura do conteúdo.
      el.style.height = '0px';
      void el.offsetHeight;                 // força reflow p/ o browser registrar o 0px
      el.style.height = `${target}px`;
    } else {
      // Fixa a altura atual (px) e anima até 0.
      el.style.height = `${target}px`;
      void el.offsetHeight;
      el.style.height = '0px';
    }
  }

  /**
   * Altura natural do conteúdo, medida em `height: auto` com a transição
   * desligada. Robusto p/ qualquer ancoragem (ex.: `justify-content: flex-end`,
   * onde o conteúdo transborda pra CIMA e `scrollHeight` não o contabiliza).
   */
  private contentHeight(): number {
    const el = this.el.nativeElement;
    const prevProp = el.style.transitionProperty;
    const prevHeight = el.style.height;
    el.style.transitionProperty = 'none';
    el.style.height = 'auto';
    const h = el.offsetHeight;
    el.style.height = prevHeight;
    el.style.transitionProperty = prevProp;
    void el.offsetHeight;                    // reflow p/ "esquecer" o height auto
    return h;
  }
}
